<script setup lang="ts">
import { settings } from '@/core/Settings';
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
	position: 'left' | 'right';
	height?: number;
}>();

const emit = defineEmits<{
	click: [];
}>();

const tooltipContainer = ref<HTMLElement | null>(null);

const textSize = computed(() => {
	if (!props.height) return { title: '24px', subtitle: '16px', base: 0 };
	const baseSize = props.height * 0.2;
	return {
		title: `${baseSize * 0.9}px`,
		subtitle: `${baseSize * 1.1}px`,
		base: baseSize
	};
});

const updateHeight = () => {
	if (props.height && tooltipContainer.value) {
		tooltipContainer.value.style.height = `${props.height * 0.9}px`;
	}
};

onMounted(() => {
	updateHeight();
	// Wait for DOM update, then recalculate
	// nextTick(() => {
	// 	updateHeight();
	// });
});

watch(() => props.height, updateHeight);

const handleClick = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
	emit('click');
};
</script>

<template>
	<Transition :name="position === 'left' ? 'slide-left' : 'slide-right'" appear>
		<div
			ref="tooltipContainer"
			:class="['tooltip-container', `tooltip-${position}`]"
			:style="{ '--base-size': `${textSize.base}px` }"
			@mousedown.prevent.stop="handleClick"
		>
			<span class="tooltip-text">
				<strong class="uppercase" :style="{ fontSize: textSize.title }">{{
					t('widget.subscription_popup_tip_title')
				}}</strong>
				<span class="subtitle" :style="{ fontSize: textSize.subtitle }">{{
					t('widget.subscription_popup_tip_subtitle', { value: settings.comp_title })
				}}</span>
			</span>
		</div>
	</Transition>
</template>

<style scoped>
.tooltip-container {
	position: absolute !important;
	display: flex;
	align-items: center;
	--background: linear-gradient(90deg, #ff7a00 0%, #ff5c00 100%) !important;
	background: rgba(0, 0, 0, 0.6);
	color: #ffcc29;
	white-space: nowrap;
	cursor: pointer;
	gap: 12px;
	z-index: 1000;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
	border: none;
	top: 45%;
	transform: translateY(-45%);
}

.tooltip-left {
	right: 50%;
	padding: calc(var(--base-size) * 0.5) calc(50% + var(--base-size)) calc(var(--base-size) * 0.5)
		calc(var(--base-size) * 1.5) !important;
	border-radius: 999px 0 0 999px;
}

.tooltip-right {
	left: 50%;
	padding: calc(var(--base-size) * 0.5) calc(var(--base-size) * 1.5) calc(var(--base-size) * 0.5)
		calc(50% + var(--base-size)) !important;
	border-radius: 0 999px 999px 0;
}

/* Left slide transition */
.slide-left-enter-active,
.slide-left-leave-active {
	transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-left-enter-from,
.slide-left-leave-to {
	opacity: 0;
	transform: translate(-40px, -45%);
}

/* Right slide transition */
.slide-right-enter-active,
.slide-right-leave-active {
	transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-right-enter-from,
.slide-right-leave-to {
	opacity: 0;
	transform: translate(40px, -45%);
}

.tooltip-text {
	display: flex;
	flex-direction: column;
	gap: 2px;
	--color: white;
	line-height: 1.2;
}

.tooltip-text strong {
	font-weight: 700;
}

.tooltip-text .subtitle {
	font-weight: 500;
}

.tooltip-arrow {
	display: flex;
	align-items: center;
	height: 100%;
}

.tooltip-arrow svg {
	height: 60%;
	width: auto;
	color: white;
}

.tooltip-left .tooltip-arrow {
	transform: rotate(180deg);
}
</style>
